import { create } from "zustand";
import {
  CredentialSource,
  VpnDebugClassification,
  VpnDebugStep,
  VpnDebugStepId,
  VpnDebugTargets,
} from "../vpnDebugInterfaces";
import { DEFAULT_ONVIF_PORT, DEFAULT_RTSP_PORT } from "../vpnDebugConstants";

/**
 * State for one diagnostic run, plus the target form that starts it.
 *
 * The orchestration in runVpnDebugLadder reads and writes this store through
 * `useVpnDebuggerStore.getState()` rather than through hook values. That is
 * required, not stylistic: `runToken` has to be readable *synchronously* inside
 * promise callbacks so a late response from a superseded run can be dropped, and
 * a value captured from `useStore()` would be the stale one from render time.
 */
interface VpnDebuggerState {
  // ---- target form -------------------------------------------------------
  tunnelId: number | null;
  setTunnelId: (value: number | null) => void;

  targets: VpnDebugTargets | null;
  setTargets: (value: VpnDebugTargets | null) => void;

  targetsLoading: boolean;
  setTargetsLoading: (value: boolean) => void;

  targetsError: string;
  setTargetsError: (value: string) => void;

  selectedCameraId: number | null;
  setSelectedCameraId: (value: number | null) => void;

  host: string;
  setHost: (value: string) => void;

  rtspPort: string;
  setRtspPort: (value: string) => void;

  onvifPort: string;
  setOnvifPort: (value: string) => void;

  username: string;
  setUsername: (value: string) => void;

  password: string;
  setPassword: (value: string) => void;

  /** Which record the prefilled credential came from, resolved client-side. */
  credentialSource: CredentialSource;
  /** True when the stored password exists but is masked (no view_passwords). */
  credentialMasked: boolean;
  /**
   * True once the operator has changed either field away from what was
   * prefilled. It is the switch between the two request shapes: untouched runs
   * send `camera_id` and let the server read the secret itself, while an edited
   * one has to go down the manual `host` path, which is the only way the server
   * will honour a supplied credential over a stored one.
   */
  credentialEdited: boolean;
  /** Applies a prefill without marking the credential as operator-edited. */
  applyPrefilledCredential: (value: {
    username: string;
    password: string;
    source: CredentialSource;
    masked: boolean;
  }) => void;
  setCredentialEdited: (value: boolean) => void;

  // ---- run state ---------------------------------------------------------
  /** Monotonic. Every write from a run checks it against its own captured copy. */
  runToken: number;
  nextRunToken: () => number;

  steps: Partial<Record<VpnDebugStepId, VpnDebugStep>>;
  setSteps: (value: Partial<Record<VpnDebugStepId, VpnDebugStep>>) => void;

  classification: VpnDebugClassification | null;
  setClassification: (value: VpnDebugClassification | null) => void;

  /**
   * Steps the server has sent in a customer payload, or null for an operator
   * (or before the first result). The customer projection withholds some steps
   * on purpose - `tunnel_config`'s every remedy is internal - so the panel must
   * not draw a row for them. Learned from the payload rather than hard-coded,
   * so the allowlist stays the server's. Survives `resetRun`: the audience does
   * not change between runs, and the row should not flash back on each one.
   */
  customerStepIds: VpnDebugStepId[] | null;
  setCustomerStepIds: (value: VpnDebugStepId[] | null) => void;

  running: boolean;
  setRunning: (value: boolean) => void;

  /** Human-readable phase, shown in the progress banner while `running`. */
  stage: string;
  setStage: (value: string) => void;

  /** Steps whose plane has not reported yet — rendered as Running, not Skipped. */
  pendingSteps: VpnDebugStepId[];
  setPendingSteps: (value: VpnDebugStepId[]) => void;

  statusMessage: string;
  statusIsError: boolean;
  setStatus: (message: string, isError?: boolean) => void;

  /**
   * Whether to offer the packet-flow window.
   *
   * Set when `tunnel_status` fails with a stale handshake, because that is the
   * only case where watching the wire for 30s tells you something the ladder
   * has not already said. Not run automatically: it costs a 30s window, and the
   * useful way to spend it is while the customer restarts their tunnel.
   */
  flowOffered: boolean;
  setFlowOffered: (value: boolean) => void;

  /**
   * The tunnel plane's task id for this run, kept so the flow window can hand
   * it back. The server reads that run's evidence to recover the peer's
   * last-known endpoint; without it there is no address to attribute arriving
   * packets to and the step can only report itself inconclusive.
   */
  flowTunnelTaskId: string;
  setFlowTunnelTaskId: (value: string) => void;

  /** Clears the ladder but keeps the target form. */
  resetRun: () => void;
  reset: () => void;
}

const initialRunState = {
  steps: {} as Partial<Record<VpnDebugStepId, VpnDebugStep>>,
  classification: null as VpnDebugClassification | null,
  running: false,
  stage: "",
  pendingSteps: [] as VpnDebugStepId[],
  flowOffered: false,
  flowTunnelTaskId: "",
  statusMessage: "",
  statusIsError: false,
};

const initialState = {
  customerStepIds: null as VpnDebugStepId[] | null,
  tunnelId: null as number | null,
  targets: null as VpnDebugTargets | null,
  targetsLoading: false,
  targetsError: "",
  selectedCameraId: null as number | null,
  host: "",
  rtspPort: DEFAULT_RTSP_PORT,
  onvifPort: DEFAULT_ONVIF_PORT,
  username: "",
  password: "",
  credentialSource: "none" as CredentialSource,
  credentialMasked: false,
  credentialEdited: false,
  runToken: 0,
  ...initialRunState,
};

export const useVpnDebuggerStore = create<VpnDebuggerState>((set, get) => ({
  ...initialState,

  setTunnelId: (value) => set({ tunnelId: value }),
  setTargets: (value) => set({ targets: value }),
  setTargetsLoading: (value) => set({ targetsLoading: value }),
  setTargetsError: (value) => set({ targetsError: value }),
  setSelectedCameraId: (value) => set({ selectedCameraId: value }),
  setHost: (value) => set({ host: value }),
  setRtspPort: (value) => set({ rtspPort: value }),
  setOnvifPort: (value) => set({ onvifPort: value }),
  // Any direct edit is operator intent, so it flips the override switch.
  setUsername: (value) => set({ username: value, credentialEdited: true }),
  setPassword: (value) => set({ password: value, credentialEdited: true }),

  applyPrefilledCredential: ({ username, password, source, masked }) =>
    set({
      username,
      password,
      credentialSource: source,
      credentialMasked: masked,
      credentialEdited: false,
    }),
  setCredentialEdited: (value) => set({ credentialEdited: value }),

  nextRunToken: () => {
    const token = get().runToken + 1;
    set({ runToken: token });
    return token;
  },

  setSteps: (value) => set({ steps: value }),
  setClassification: (value) => set({ classification: value }),
  setCustomerStepIds: (value) => set({ customerStepIds: value }),
  setRunning: (value) => set({ running: value }),
  setStage: (value) => set({ stage: value }),
  setPendingSteps: (value) => set({ pendingSteps: value }),
  setStatus: (message, isError = false) =>
    set({ statusMessage: message, statusIsError: isError }),
  setFlowOffered: (value) => set({ flowOffered: value }),
  setFlowTunnelTaskId: (value) => set({ flowTunnelTaskId: value }),

  resetRun: () => set({ ...initialRunState }),
  // Deliberately keeps runToken: resetting it would let a still-in-flight run
  // from before the reset match the new token and write into the fresh ladder.
  reset: () => set({ ...initialState, runToken: get().runToken }),
}));
